import { ReactNode } from 'react';
import { MotionConfig } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
// @mui
import { Box } from '@mui/material';
//
import { PRODUCTS } from '../../data/products';
import { ATMOS, GUTTER, STAGE } from '../../theme/stage';
import { ANCHOR, PageHead, SHARE_IMAGE } from './copy';
import SiteFooter from './SiteFooter';
import SiteHeader from './SiteHeader';
import SkipLink from './SkipLink';

// ----------------------------------------------------------------------

type Props = {
  head: PageHead;
  children: ReactNode;
};

export default function PageShell({ head, children }: Props) {
  return (
    <>
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={head.url} />
        <meta property="og:image" content={SHARE_IMAGE.src} />
        <meta property="og:image:width" content={String(SHARE_IMAGE.width)} />
        <meta property="og:image:height" content={String(SHARE_IMAGE.height)} />
        <meta property="og:image:alt" content={SHARE_IMAGE.alt} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={head.url} />
      </Helmet>

      <MotionConfig reducedMotion="user">
        <Box
          sx={{
            position: 'relative',
            minHeight: '100%',
            bgcolor: STAGE.ground,
            color: STAGE.ink,
            '--gutter': GUTTER,
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: 40,
              pointerEvents: 'none',
              backgroundImage: ATMOS.grain,
              backgroundSize: '160px 160px',
              opacity: ATMOS.grainOpacity,
              '@media print': { display: 'none' },
              '@media (forced-colors: active)': { display: 'none' },
            },
            '@media print': {
              bgcolor: '#FFFFFF',
              color: '#000000',
              '& [data-chrome]': { display: 'none !important' },
            },
          }}
        >
          <SkipLink />
          <SiteHeader products={PRODUCTS} />

          <Box component="main" id={ANCHOR.main} sx={{ '&:focus': { outline: 'none' } }}>
            {children}
          </Box>

          <SiteFooter products={PRODUCTS} />
        </Box>
      </MotionConfig>
    </>
  );
}
